import { assertUnreachable, ConfigConstants, isDefined, Mutable } from '@evorait/pace-core';
import moment from 'moment';
import { CheckpointSelection } from '../types/checkpoint';
import { Choice, CHOICE_TYPE, Choices } from '../types/choice';
import { ContentBlock, ContentId } from '../types/content-block';
import { ContentBlocks } from '../types/content-blocks';
import { CONTENT_TYPE, ContentChild } from '../types/content-child';
import { ContentResults } from '../types/content-results';
import { Copyright } from '../types/copyright';
import { CustomerIdentification, initialCosimaCustomerIdentification } from '../types/customer-identification';
import { CustomerIdentificationInputField } from '../types/customer-identification-input-field';
import { DatePerformed } from '../types/date-performed';
import {
    DefectServicePartIdentification,
    DefectServicePartInputField,
} from '../types/defect-service-part-identification';
import { DefectiveComponentReplacement, ExchangeableComponent } from '../types/defective-component-replacement';
import { DropDownOptionsFunctionalElement } from '../types/drop-down-options';
import { initialFormFilters } from '../types/form-filter';
import { FormTypeFilters, initialFormTypeFilters } from '../types/form-type-filters';
import { FormsConstants } from '../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE, FunctionalElement } from '../types/functional-element';
import { FunctionalElements } from '../types/functional-elements';
import { GroupOption } from '../types/group-option';
import { Heading } from '../types/heading';
import { Headings } from '../types/headings';
import { MaterialSerialNumber } from '../types/material-serial-number';
import { CosimaOption, isCosimaOption, OptionDependencies } from '../types/option';
import { OptionSetGroup } from '../types/option-set-group';
import { OrgData } from '../types/org-data';
import { RemarksSection } from '../types/remarks-section';
import { Signature } from '../types/signature';
import { StandardFunctionalElement } from '../types/standard-functional-element';
import { DEFICIENCY_SELECTION, SystemCondition } from '../types/system-condition';
import { TypeFilter } from '../types/type-filter';
import { ValueFunctionalElement } from '../types/value-functional-element';
import { AnswerUtilities } from './answer-utilities';
import { FormUtilities } from './form-utilities';
import { getChildNodes, getFirstNode } from './xml-dom-utilities';
import {
    initialMeasuringEquipments,
    MeasuringEquipmentHeaderColumn,
    MeasuringEquipmentRowColumn,
    MeasuringEquipments,
    MeasuringEquipmentTableHeader,
    MeasuringEquipmentTableRow,
} from '../types/measuring-equipments';

// try to parse the retrieved date from a form in other languages, as this might have been saved in another language.
function parseDateInOneOfTheKnownLanguages(retrievedDate: string): moment.Moment | undefined {
    const languageIdentifiers = ['en', 'de', 'fr', 'it'];

    let validMomentDate: moment.Moment | undefined;

    for (const lang of languageIdentifiers) {
        moment.locale(lang);
        const momentDate = moment(retrievedDate, ConfigConstants.formatPatternDateCosima);
        if (momentDate.isValid()) {
            validMomentDate = momentDate;
            // reset back the language to english
            break;
        }
    }
    // reset the language to english, as all the newer apps should only use english
    moment.locale(languageIdentifiers[0]);
    return validMomentDate;
}

/**
 *  Internally assigned, unique ID for new content blocks to help addressing them.
 * Mae public to be able to reset it for tests.
 */
let xmlNodeIdCounter = 0;

function multipleSignaturesAvailable(
    functionalElements: Mutable<FunctionalElements>,
    inputType: FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE | FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
): boolean {
    return (
        Object.values(functionalElements).filter((functionalElement) => functionalElement.type === inputType).length > 1
    );
}

function hasPdfFormOutsideDateRange(eResult: Element, fromDate: moment.Moment, tillDate: moment.Moment): boolean {
    const modifiedDateString = eResult.getAttribute('modified') ?? '';
    const modified = moment(modifiedDateString);
    return modified.isBefore(fromDate) || modified.isAfter(tillDate);
}

function getTopicMaterialNumbers(doc: XMLDocument, node: Node): Element[] {
    const materialNumberPath = './materialnumber';
    return getChildNodes(doc, node, materialNumberPath);
}

function getHeading(doc: XMLDocument, node: Node, parentId: ContentId): Heading {
    const sectionPath = './section';
    const sectionNode = getFirstNode(doc, node, sectionPath);
    const section = sectionNode?.textContent ?? '';

    const headingPath = './heading';
    const headingNode = getFirstNode(doc, node, headingPath);
    const heading = headingNode?.textContent ?? '';

    return { chapterNumber: section, heading, id: xmlNodeIdCounter++, parentId };
}

function getChoice(doc: XMLDocument, choicesNode: Node, type: CHOICE_TYPE): Choice | undefined {
    const choiceNode = getFirstNode(doc, choicesNode, `./${type}`.toLowerCase());
    return isDefined(choiceNode)
        ? {
              xmlId: choiceNode.getAttribute('id') ?? '',
              label: choiceNode.getAttribute('label') ?? '',
              selected: choiceNode.getAttribute('selected') === 'true',
              selectedInMaster: choiceNode.getAttribute('selected') === 'true',
              type,
          }
        : undefined;
}

function getChoices(doc: XMLDocument, node: Element): Choices {
    const choicesNode = getFirstNode(doc, node, './choices');
    const choices: Mutable<Choices> = {};

    if (!isDefined(choicesNode)) {
        return choices;
    }

    const choicePositive = getChoice(doc, choicesNode, CHOICE_TYPE.CHOICE_POSITIVE);
    const choiceNegative = getChoice(doc, choicesNode, CHOICE_TYPE.CHOICE_NEGATIVE);
    const choiceNa = getChoice(doc, choicesNode, CHOICE_TYPE.CHOICE_NA);

    if (isDefined(choicePositive)) {
        choices.choicePositive = choicePositive;
    }
    if (isDefined(choiceNegative)) {
        choices.choiceNegative = choiceNegative;
    }
    if (isDefined(choiceNa)) {
        choices.choiceNa = choiceNa;
    }

    return choices;
}

function getRemarks(
    doc: XMLDocument,
    node: Element,
): Required<Pick<FunctionalElement, 'remarks' | 'remarksXmlId' | 'remarksLabel'>> {
    const xmlId = node.getAttribute('id');
    const remarksElement = getFirstNode(doc, node, './remark');
    if (!isDefined(remarksElement)) {
        throw new Error(`CosimaXmlUtilities: getRemarks: No remarks element found for e-result with id '${xmlId}'`);
    }
    const remarks = remarksElement.textContent ?? '';
    const remarksLabel = remarksElement?.getAttribute('label') ?? '';
    const remarksXmlId = remarksElement?.getAttribute('id') ?? '';
    if (!isDefined(remarksXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getRemarks: Attribute id not defined on remark field in e-result with id '${xmlId}'!`,
        );
    }

    return { remarks, remarksLabel, remarksXmlId };
}

function getDescription(doc: XMLDocument, node: Node): string {
    const descriptionNode = getFirstNode(doc, node, './description');
    return descriptionNode?.textContent ?? '';
}

function getVisibility(element: Element): boolean {
    const visibility = element.getAttribute('visibility');

    // if t he visibility attribute is not found on the topic
    // we d isplay the topic by any means
    return isDefined(visibility) ? visibility === 'yes' : true;
}

function getStandardFunctionalElement(
    doc: XMLDocument,
    element: Element,
    parentId: ContentId,
    functionalElementType: FUNCTIONAL_ELEMENT_TYPE.STANDARD | FUNCTIONAL_ELEMENT_TYPE.REMARK,
): StandardFunctionalElement {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(`CosimaXmlUtilities: getStandardFunctionalElement: Attribute id not defined on e-result!`);
    }

    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const uniqueId = element.getAttribute('unique-id') ?? '';
    const label = getDescription(doc, element);
    const elementType = FormUtilities.getElementType(filter);
    const choices = getChoices(doc, element);

    const remarksElement = getFirstNode(doc, element, './remark');

    let remarks: string | undefined;
    let remarksLabel: string | undefined;
    let remarksXmlId: string | undefined;

    // the  standard functional element within the system condition does not contain remarks.
    if (isDefined(remarksElement)) {
        const remarksElementObject = getRemarks(doc, element);
        remarks = remarksElementObject.remarks;
        remarksLabel = remarksElementObject.remarksLabel;
        remarksXmlId = remarksElementObject.remarksXmlId;
    }

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        label,
        choices,
        remarks,
        remarksLabel,
        remarksXmlId,
        elementType,
        contentLoadedFromXml:
            (remarks && remarks.length > 0) ||
            Object.values(choices)
                .filter(isDefined)
                .some((c) => c.selected),
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: functionalElementType,
        uniqueId,
    };
}

function getGroupAnswers(doc: XMLDocument, contextNode: Element): GroupOption[] {
    const options: GroupOption[] = [];
    const optionNodes = getChildNodes(doc, contextNode, './answer');

    if (optionNodes.length === 0) {
        return options;
    }

    return optionNodes.map((answerNode) => {
        const id = answerNode.getAttribute('id') ?? '';
        const name = answerNode.getAttribute('name') ?? '';
        const label = answerNode.getAttribute('label') ?? '';
        const type = answerNode.getAttribute('type') ?? '';
        const selected = answerNode.getAttribute('selected') ?? false;
        const evaluation = answerNode.getAttribute('evaluation') ?? '';

        const newAnswer: GroupOption = {
            id,
            name,
            label,
            type,
            selected: selected === 'true',
            evaluation,
        };

        return newAnswer;
    });
}

function getGroup(doc: XMLDocument, groupNode: Element): OptionSetGroup {
    const groupTitleNode = getFirstNode(doc, groupNode, './title');

    if (!isDefined(groupTitleNode)) {
        throw new Error(`CosimaXmlUtilities: getGroup: Could not find node path for title !`);
    }

    const groupTitle = groupTitleNode?.textContent ?? '';
    const answers = getGroupAnswers(doc, groupNode);

    return {
        title: groupTitle,
        options: answers,
    };
}
function getAnswerSet(doc: XMLDocument, contextNode: Element): OptionSetGroup[] {
    const groupNodes = getChildNodes(doc, contextNode, './answerset/group');

    if (groupNodes.length === 0) {
        throw new Error(`CosimaXmlUtilities: getAnswerSet: No answer groups available !`);
    }

    const groups: OptionSetGroup[] = [];

    groupNodes.forEach((groupNode) => {
        const group = getGroup(doc, groupNode);
        groups.push(group);
    });

    return groups;
}

function getDropDownOptionsFunctionalElement(
    doc: XMLDocument,
    element: Element,
    parentId: ContentId,
): DropDownOptionsFunctionalElement {
    const xmlId = element.getAttribute('id');
    const functionAttribute = element.getAttribute('function') ?? '';
    if (!isDefined(xmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getDropDownAnswersFunctionalElement: Attribute id not defined on e-result!`,
        );
    }

    const uniqueId = element.getAttribute('unique-id') ?? '';
    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const label = getDescription(doc, element);
    const elementType = FormUtilities.getElementType(filter);
    const choices = getChoices(doc, element);

    const remarksElement = getFirstNode(doc, element, './remark');

    let remarks: string | undefined;
    let remarksLabel: string | undefined;
    let remarksXmlId: string | undefined;
    let remarksAnswerSet: OptionSetGroup[] = [];

    // the  standard functional element within the system condition does not contain remarks.
    if (isDefined(remarksElement)) {
        const remarksElementObject = getRemarks(doc, element);
        remarks = remarksElementObject.remarks;
        remarksLabel = remarksElementObject.remarksLabel;
        remarksXmlId = remarksElementObject.remarksXmlId;
        remarksAnswerSet = getAnswerSet(doc, element);
    }

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        label,
        function: functionAttribute,
        choices,
        remarks,
        remarksLabel,
        remarksXmlId,
        elementType,
        contentLoadedFromXml:
            (remarks && remarks.length > 0) ||
            Object.values(choices)
                .filter(isDefined)
                .some((c) => c.selected),
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS,
        optionSet: remarksAnswerSet,
        uniqueId,
    };
}

function getSignatureFunctionalElement(
    doc: XMLDocument,
    element: Element,
    parentId: ContentId,
    functionalElementType: FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE | FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
): Signature {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(`CosimaXmlUtilities: getSignatureFunctionalElement: Attribute id not defined on e-result!`);
    }

    const description = getDescription(doc, element);
    const uniqueId = element.getAttribute('unique-id') ?? '';
    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const elementType = FormUtilities.getElementType(filter);

    const choicesElement = getFirstNode(doc, element, './choices');

    let choices: Mutable<Choices> | undefined;

    if (isDefined(choicesElement)) {
        choices = getChoices(doc, element);

        // for  employee signature the choice n.a should always be hidden.
        if (functionalElementType === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE && isDefined(choices.choiceNa)) {
            delete choices.choiceNa;
        }
    }

    const signatureElement = getFirstNode(doc, element, './signature');
    if (!isDefined(signatureElement)) {
        throw new Error(
            `CosimaXmlUtilities: getSignatureFunctionalElement: e-result with id '${xmlId}' has no signature element!`,
        );
    }

    const label = signatureElement.getAttribute('label') ?? '';

    const entityPath = signatureElement.getAttribute('href') ?? '';
    if (!isDefined(entityPath)) {
        throw new Error(
            `CosimaXmlUtilities: getSignatureFunctionalElement: e-result with id '${xmlId}' has no signature entity path!`,
        );
    }
    const height = parseInt(signatureElement.getAttribute('height') ?? '0', 10);
    const width = parseInt(signatureElement.getAttribute('width') ?? '0', 10);

    const dateElement = getFirstNode(doc, element, './date');
    if (!isDefined(dateElement)) {
        throw new Error(
            `CosimaXmlUtilities: getSignatureFunctionalElement: e-result with id '${xmlId}' has no date element!`,
        );
    }

    const date = dateElement.textContent ?? '';
    const dateXmlId = dateElement.getAttribute('id');
    if (!isDefined(dateXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getSignatureFunctionalElement: No dateXmlId found for e-result with id '${xmlId}'`,
        );
    }

    const nameElement = getFirstNode(doc, element, './name');
    if (!isDefined(nameElement)) {
        throw new Error(
            `CosimaXmlUtilities: getSignatureFunctionalElement: e-result with id '${xmlId}' has no name element!`,
        );
    }
    const nameXmlId = nameElement.getAttribute('id');
    if (!isDefined(nameXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getSignatureFunctionalElement: No nameXmlId found for e-result with id '${xmlId}'`,
        );
    }

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        description,
        choices,
        label,
        entity: '',
        entityPath,
        signature: '',
        height,
        width,
        signatureDataPoints: [], // not  stored in XML, but needed for signature component
        nameXmlId,
        firstName: '', // we d o not restore signature names, as the user is supposed to re-enter the signature every time.
        lastName: '', // we d o not restore signature names, as the user is supposed to re-enter the signature every time.
        date,
        dateXmlId,
        emailRequired: false, // reev aluate if we need this in cosima when implementing the customer_signature
        email: '', // not  stored in XML, but needed for signature component
        elementType,
        contentLoadedFromXml: false,
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: functionalElementType,
        uniqueId,
    };
}

function getComponentFunctionalElement(doc: XMLDocument, element: Element, parentId: ContentId): MaterialSerialNumber {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(`CosimaXmlUtilities: getComponentFunctionalElement: Attribute id not defined on e-result!`);
    }

    const description = getDescription(doc, element);
    const uniqueId = element.getAttribute('unique-id') ?? '';
    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const elementType = FormUtilities.getElementType(filter);

    const choices = getChoices(doc, element);

    const componentNode = getFirstNode(doc, element, './component');
    if (!isDefined(componentNode)) {
        throw new Error(
            `CosimaXmlUtilities: getComponentFunctionalElement: e-result with id '${xmlId}' has no component element!`,
        );
    }

    const materialNumberElement = getFirstNode(doc, componentNode, './matno');
    if (!isDefined(materialNumberElement)) {
        throw new Error(
            `CosimaXmlUtilities: getComponentFunctionalElement: e-result with id '${xmlId}' has no matno element!`,
        );
    }

    const materialValue = materialNumberElement.textContent ?? '';
    const materialLabel = materialNumberElement.getAttribute('label') ?? '';
    const materialXmlId = materialNumberElement.getAttribute('id');
    if (!isDefined(materialXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getComponentFunctionalElement: No materialXmlId found for e-result with id '${xmlId}'`,
        );
    }

    const serialNumberElement = getFirstNode(doc, componentNode, './serialno');
    if (!isDefined(serialNumberElement)) {
        throw new Error(
            `CosimaXmlUtilities: getComponentFunctionalElement: e-result with id '${xmlId}' has no matno element!`,
        );
    }
    const serialValue = serialNumberElement.textContent ?? '';
    const serialLabel = serialNumberElement.getAttribute('label') ?? '';
    const serialXmlId = serialNumberElement.getAttribute('id');
    if (!isDefined(serialXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getComponentFunctionalElement: No serialXmlId found for e-result with id '${xmlId}'`,
        );
    }

    const { remarks, remarksLabel, remarksXmlId } = getRemarks(doc, element);

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        description,
        choices,
        materialValue,
        materialLabel,
        materialXmlId,
        serialValue,
        serialLabel,
        serialXmlId,
        remarks,
        remarksLabel,
        remarksXmlId,
        elementType,
        descriptionLabel: '', // only used in Astoria
        descriptionXmlId: '', // only used in Astoria
        descriptionValue: '', // only used in Astoria
        contentLoadedFromXml:
            remarks.length > 0 ||
            Object.values(choices)
                .filter(isDefined)
                .some((c) => c.selected),
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.COMPONENT,
        uniqueId,
    };
}

function getDateFunctionalElement(doc: XMLDocument, element: Element, parentId: ContentId): DatePerformed {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(`CosimaXmlUtilities: getDateFunctionalElement: Attribute id not defined on e-result!`);
    }

    const readFunctionType = element.getAttribute('function');
    let functionType: FormsConstants.FUNCTION_TYPE | undefined;

    if (isDefined(readFunctionType)) {
        functionType =
            readFunctionType === FormsConstants.FUNCTION_TYPE.OLDER
                ? FormsConstants.FUNCTION_TYPE.OLDER
                : FormsConstants.FUNCTION_TYPE.NEWER;
    }

    const description = getDescription(doc, element);

    const choices = getChoices(doc, element);

    const uniqueId = element.getAttribute('unique-id') ?? '';
    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const elementType = FormUtilities.getElementType(filter);

    const { remarks, remarksLabel, remarksXmlId } = getRemarks(doc, element);

    const dateElement = getFirstNode(doc, element, './date');
    if (!isDefined(dateElement)) {
        throw new Error(
            `CosimaXmlUtilities: getDateFunctionalElement: No date element found for e-result with id '${xmlId}'`,
        );
    }

    const dateLabel = dateElement.getAttribute('label') ?? '';
    const dateXmlId = dateElement.getAttribute('id');
    if (!isDefined(dateXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getDateFunctionalElement: Attribute id not defined on date field in e-result with id '${xmlId}'!`,
        );
    }

    // the date being stored in the xml has the format "DD-MMM-YYYY"
    const dateRetrieved = dateElement.textContent ?? '';
    // we need to convert it to ISO, to assure that the date gets retrieved and rebuilt correctly for older browsers(EdgeHTMl - cordova windows)

    let date: string;

    if (dateRetrieved === FormsConstants.NA) {
        date = dateRetrieved;
    } else if (dateRetrieved.length > 0) {
        // some interims still contain dates saved in german, or french
        // we need to retrieve the date irrespective of language
        const momentDate = parseDateInOneOfTheKnownLanguages(dateRetrieved);

        if (isDefined(momentDate) && momentDate.isValid()) {
            date = momentDate.toISOString();
        } else {
            // Some of the forms which were filled out with older apps still have the date saved as an ISOString in the locale language, instead of DD-MMM-YYYY
            const parsedDateFromIsoString = moment(dateRetrieved);
            if (parsedDateFromIsoString.isValid()) {
                date = parsedDateFromIsoString.toISOString();
            } else {
                date = '';
            }
        }
    } else {
        date = '';
    }

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        dateXmlId,
        description,
        date,
        dateLabel,
        choices,
        remarks,
        remarksLabel,
        remarksXmlId,
        elementType,
        function: functionType ? functionType : undefined,
        contentLoadedFromXml:
            remarks.length > 0 ||
            Object.values(choices)
                .filter(isDefined)
                .some((c) => c.selected) ||
            date.length > 0,
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.DATE,
        uniqueId,
    };
}

function getValueFunctionalElement(doc: XMLDocument, element: Element, parentId: ContentId): ValueFunctionalElement {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(`CosimaXmlUtilities: getValueFunctionalElement: Attribute id not defined on e-result!`);
    }

    const description = getDescription(doc, element);

    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const uniqueId = element.getAttribute('unique-id') ?? '';
    const elementType = FormUtilities.getElementType(filter);
    const choices = getChoices(doc, element);

    const { remarks, remarksLabel, remarksXmlId } = getRemarks(doc, element);

    const valueElement = getFirstNode(doc, element, './value');
    if (!isDefined(valueElement)) {
        throw new Error(`CosimaXmlUtilities: getValueFunctionalElement: value element could not be found!`);
    }

    const functionalAttribute = element.getAttribute('function');
    const valueXmlId = valueElement.getAttribute('id');
    if (!isDefined(valueXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getValueFunctionalElement: Attribute id not defined on value field in e-result with id '${xmlId}'!`,
        );
    }

    const label = valueElement.getAttribute('label') ?? '';
    const value = valueElement.textContent ?? '';
    const unit = valueElement.getAttribute('unit') ?? '';

    const upperLimitValue = parseFloat(valueElement?.getAttribute('upperlimit') ?? '');
    const lowerLimitValue = parseFloat(valueElement?.getAttribute('lowerlimit') ?? '');

    const expectedValueElement = getFirstNode(doc, element, './expectedvalue');
    if (!isDefined(expectedValueElement)) {
        throw new Error(
            `CosimaXmlUtilities: getValueFunctionalElement: element expectedvalue is not defined on e-result with id '${xmlId}'!`,
        );
    }
    const expectedValueText = expectedValueElement.textContent ?? undefined;

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        label,
        description,
        value,
        valueXmlId,
        expectedValueText,
        upperLimit: isNaN(upperLimitValue) ? undefined : upperLimitValue,
        lowerLimit: isNaN(lowerLimitValue) ? undefined : lowerLimitValue,
        unit,
        choices,
        remarks,
        remarksLabel,
        remarksXmlId,
        elementType,
        contentLoadedFromXml:
            remarks.length > 0 ||
            Object.values(choices)
                .filter(isDefined)
                .some((c) => c.selected) ||
            value.length > 0,
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.VALUE,
        disabledNACheckbox: false,
        function: isDefined(functionalAttribute) ? functionalAttribute : undefined,
        uniqueId,
    };
}

function getOption(doc: XMLDocument, element: Element, parentId: ContentId): CosimaOption {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(`CosimaXmlUtilities: getOption: Attribute id not defined on e-result!`);
    }
    const label = getDescription(doc, element);
    const choices = getChoices(doc, element);

    const yesLabel = choices.choicePositive?.label ?? '';
    const noLabel = choices.choiceNegative?.label ?? '';

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        label,
        yesLabel,
        noLabel,
        choices,
        // OPTI ON never has a controlling checkpoint, since it functions as a controlling element itself
        controllingCheckpoint: '',
        contentLoadedFromXml: Object.values(choices)
            .filter(isDefined)
            .some((c) => c.selected),
        type: FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT,
        controllingOption: undefined,
        disabled: false,
        visible: getVisibility(element),
        parentId,
    };
}
function getExchangeableComponentFromDefectiveComponentReplacement(
    doc: XMLDocument,
    node: Node,
    type: string,
    parentXmlId: string,
): ExchangeableComponent {
    const componentNode = getFirstNode(doc, node, `./component[@type='${type}']`);
    if (!isDefined(componentNode)) {
        throw new Error(
            `CosimaXmlUtilities: getExchangeableComponentFromDefectiveComponentReplacement: No component element found for e-result with id '${parentXmlId}' and type '${type}'`,
        );
    }

    let componentXmlId = componentNode.getAttribute('id');
    if (!isDefined(componentXmlId)) {
        componentXmlId = '';
    }

    const labelNode = getFirstNode(doc, componentNode, `./label`);
    const label = labelNode?.textContent ?? '';

    const materialNoElement = getFirstNode(doc, componentNode, `./matno`);
    if (!isDefined(materialNoElement)) {
        throw new Error(
            `CosimaXmlUtilities: getExchangeableComponentFromDefectiveComponentReplacement: No material element found for e-result with id '${parentXmlId}' and type '${type}'`,
        );
    }

    const matNoLabel = materialNoElement.getAttribute('label') ?? '';
    const matNoXmlId = materialNoElement.getAttribute('id');
    if (!isDefined(matNoXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getExchangeableComponentFromDefectiveComponentReplacement: material No id not defined on date field in e-result with id '${parentXmlId}' and type '${type}'!`,
        );
    }
    const matNo = materialNoElement.textContent ?? '';

    const serialNoElement = getFirstNode(doc, componentNode, `./serialno`);
    if (!isDefined(serialNoElement)) {
        throw new Error(
            `CosimaXmlUtilities: getExchangeableComponentFromDefectiveComponentReplacement: No material element found for e-result with id '${parentXmlId}' and type '${type}'`,
        );
    }

    const serialNoLabel = serialNoElement.getAttribute('label') ?? '';
    const serialNoXmlId = serialNoElement.getAttribute('id');
    if (!isDefined(serialNoXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getExchangeableComponentFromDefectiveComponentReplacement: serial No id not defined on date field in e-result with id '${parentXmlId}' and type '${type}'!`,
        );
    }
    const serialNo = serialNoElement.textContent ?? '';

    return { componentXmlId, label, matNo, matNoLabel, matNoXmlId, serialNo, serialNoLabel, serialNoXmlId };
}

function getDefectiveComponentReplacementFunctionalElement(
    doc: XMLDocument,
    element: Element,
    parentId: ContentId,
): DefectiveComponentReplacement {
    const xmlId = element.getAttribute('id');
    if (!isDefined(xmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getDefectiveComponentReplacementFunctionalElement: Attribute id not defined on e-result!`,
        );
    }

    const description = getDescription(doc, element);
    const choices = getChoices(doc, element);

    const uniqueId = element.getAttribute('unique-id') ?? '';
    const filter = (element.getAttribute('filter') ?? '').toUpperCase();
    const elementType = FormUtilities.getElementType(filter);

    const { remarks, remarksLabel, remarksXmlId } = getRemarks(doc, element);

    const swapOutComponent = getExchangeableComponentFromDefectiveComponentReplacement(doc, element, 'swapout', xmlId);
    const swapInComponent = getExchangeableComponentFromDefectiveComponentReplacement(doc, element, 'swapin', xmlId);

    const repairTimeElement = getFirstNode(doc, element, './repairtime');
    if (!isDefined(repairTimeElement)) {
        throw new Error(
            `CosimaXmlUtilities: getDefectiveComponentReplacementFunctionalElement: No repair time element node found for e-result with id '${xmlId}'`,
        );
    }
    const repairTimeLabel = repairTimeElement.getAttribute('label') ?? '';
    const repairTimeXmlId = repairTimeElement.getAttribute('id');
    if (!isDefined(repairTimeXmlId)) {
        throw new Error(
            `CosimaXmlUtilities: getDefectiveComponentReplacementFunctionalElement: repair time id not defined on date field in e-result with id '${xmlId}'!`,
        );
    }

    const repairTime = repairTimeElement.textContent ?? '';

    return {
        id: xmlNodeIdCounter++,
        xmlId,
        description,
        choices,
        swapOutComponent,
        swapInComponent,
        repairTimeLabel,
        repairTimeXmlId,
        repairTime,
        remarks,
        remarksLabel,
        remarksXmlId,
        elementType,
        contentLoadedFromXml:
            remarks.length > 0 ||
            Object.values(choices)
                .filter(isDefined)
                .some((c) => c.selected) ||
            swapOutComponent.matNo.length > 0 ||
            swapOutComponent.serialNo.length > 0 ||
            swapInComponent.matNo.length > 0 ||
            swapInComponent.serialNo.length > 0 ||
            repairTime.length > 0,
        controllingCheckpoint: '',
        controllingOption: undefined,
        disabled: false,
        parentId,
        type: FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT,
        uniqueId,
    };
}

function getFunctionalElement(doc: XMLDocument, node: Element, parentId: ContentId): FunctionalElement | undefined {
    const xmlId = node.getAttribute('id');
    const functionalElementType = (node.getAttribute('inputtype') ?? '').toUpperCase() as FUNCTIONAL_ELEMENT_TYPE;
    if (!functionalElementType) {
        throw new Error(`CosimaXmlUtilities: getFunctionalElement: e-result with id '${xmlId}' has no inputtype!`);
    }

    switch (functionalElementType) {
        case FUNCTIONAL_ELEMENT_TYPE.STANDARD:
        case FUNCTIONAL_ELEMENT_TYPE.REMARK:
            return getStandardFunctionalElement(doc, node, parentId, functionalElementType);
        case FUNCTIONAL_ELEMENT_TYPE.COMPONENT:
            return getComponentFunctionalElement(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE:
        case FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE:
            return getSignatureFunctionalElement(doc, node, parentId, functionalElementType);
        case FUNCTIONAL_ELEMENT_TYPE.DATE:
            return getDateFunctionalElement(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.VALUE:
            return getValueFunctionalElement(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT:
            return getOption(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT:
            return getDefectiveComponentReplacementFunctionalElement(doc, node, parentId);
        case FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS:
            return getDropDownOptionsFunctionalElement(doc, node, parentId);
        // The  following functional elements do not exist in Cosima; they are just here so that the switch case coverage is complete
        case FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION:
        case FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED:
        case FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED:
        case FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT:
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO:
        case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER:
        case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE:
        case FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE:
        case FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE:
        case FUNCTIONAL_ELEMENT_TYPE.OPTION:
            throw new Error(
                `CosimaXmlUtilities: getFunctionalElement: type '${functionalElementType}' not expected in Cosima for id '${xmlId}'`,
            );

        default:
            console.warn(
                `CosimaXmlUtilities: getFunctionalElement: Encountered unknown functional element type '${functionalElementType}'`,
            );
            // TODO : Add assertUnreachable instead of returning undefined
            return undefined;
    }
}

function getFunctionalElements(doc: XMLDocument, node: Node, parentId: ContentId): FunctionalElement[] {
    const eresultPath = './e-result';
    const eresultNodes = getChildNodes(doc, node, eresultPath);

    return eresultNodes.map((e) => getFunctionalElement(doc, e, parentId)).filter(isDefined); // ensu res that unknown functional elements are not in the store
}

function getIndividualization(doc: XMLDocument): boolean {
    const individualizationPath = '/document/meta/individualization';
    const individualizationNode = getFirstNode(doc, doc, individualizationPath);
    if (!isDefined(individualizationNode)) {
        throw new Error(
            `CosimaXmlUtilities: getCopyright: Could not find node for copyright path '${individualizationPath}'`,
        );
    }
    return individualizationNode.textContent === 'true';
}

function getCustomerIdentificationFieldByElementName(
    doc: XMLDocument,
    elementName: string,
): CustomerIdentificationInputField {
    const valueElement = getFirstNode(
        doc,
        doc,
        `/document/topics/topic[@id='customeridentification']/content/${elementName}`,
    );
    if (!valueElement) {
        throw new Error(
            `CosimaXmlUtilities: getCustomerIdentificationFieldByElementName: Could not find node for field '${elementName}'`,
        );
    }
    const label = valueElement.getAttribute('label') ?? '';
    const value = valueElement.textContent ?? '';
    const customerIdentificationXMLStructure = getFirstNode(
        doc,
        doc,
        `/document/topics/topic[@id='customeridentification']/content`,
    );

    if (!customerIdentificationXMLStructure) {
        throw new Error(
            `CosimaXmlUtilities: getCustomerIdentificationFieldByElementName: Could not find node for customerIdentification structure`,
        );
    }

    const order = doc.evaluate(
        `count(./${elementName}/preceding-sibling::*)+1`,
        customerIdentificationXMLStructure,
        null,
        XPathResult.NUMBER_TYPE,
        null,
    ).numberValue;

    return {
        xmlId: elementName,
        value,
        label,
        order,
    };
}

function getCustomerIdentification(doc: XMLDocument): CustomerIdentification {
    return {
        customer1: getCustomerIdentificationFieldByElementName(doc, 'customer1'),
        customer2: getCustomerIdentificationFieldByElementName(doc, 'customer2'),
        address1: getCustomerIdentificationFieldByElementName(doc, 'address1'),
        address2: getCustomerIdentificationFieldByElementName(doc, 'address2'),
        department: getCustomerIdentificationFieldByElementName(doc, 'department'),
        room: getCustomerIdentificationFieldByElementName(doc, 'room'),
        materialNr: getCustomerIdentificationFieldByElementName(doc, 'materialno'),
        serialNr: getCustomerIdentificationFieldByElementName(doc, 'serialno'),
        orderNr: getCustomerIdentificationFieldByElementName(doc, 'orderno'),
        systemId: getCustomerIdentificationFieldByElementName(doc, 'systemid'),
        systemName: getCustomerIdentificationFieldByElementName(doc, 'systemname'),
        functionalLocation: getCustomerIdentificationFieldByElementName(doc, 'functionallocation'),
    };
}

function getRemarksSection(doc: XMLDocument): RemarksSection | undefined {
    const topicElement = getFirstNode(doc, doc, `/document/topics/topic[@id='remarks']/content`);

    if (!topicElement) {
        return undefined;
    }

    const titleNode = getFirstNode(doc, topicElement, './title');

    if (!isDefined(titleNode)) {
        throw new Error(`CosimaXmlUtilities: getRemarksSection: Could not find node path for title !`);
    }

    const title = titleNode.textContent ?? '';

    const eResultNode = getFirstNode(doc, topicElement, './e-result');

    if (!isDefined(eResultNode)) {
        throw new Error(`CosimaXmlUtilities: getRemarksSection: Could not find node path for e-result !`);
    }

    const { remarks, remarksLabel, remarksXmlId } = getRemarks(doc, eResultNode);

    return {
        remarksXmlId: remarksXmlId,
        title,
        remarksXmlLabel: remarksLabel,
        value: remarks,
    };
}

function getOrgData(doc: XMLDocument): OrgData {
    const productPath = '/document/meta/product';
    const productElement = getFirstNode(doc, doc, productPath);
    if (!isDefined(productElement)) {
        throw new Error(`CosimaXmlUtilities: getOrgData: Could not find node for product path '${productPath}'!`);
    }
    const product = productElement.textContent ?? '';

    const productTypePath = '/document/meta/producttype';
    const productTypeNode = getFirstNode(doc, doc, productTypePath);
    if (!isDefined(productTypeNode)) {
        throw new Error(
            `CosimaXmlUtilities: getOrgData: Could not find node for product type path '${productTypePath}'!`,
        );
    }
    const productType = productTypeNode.textContent ?? '';

    // in cosima the title element replaced the 'variant' element from astoria
    const variantPath = '/document/meta/title';
    const variantNode = getFirstNode(doc, doc, variantPath);
    if (!isDefined(variantNode)) {
        throw new Error(`CosimaXmlUtilities: getOrgData: Could not find node for variant path '${variantPath}'!`);
    }
    const variant = variantNode.textContent ?? '';

    const productKeytPath = '/document/meta/productkey';
    const productKeyNode = getFirstNode(doc, doc, productKeytPath);
    if (!isDefined(productKeyNode)) {
        throw new Error(
            `CosimaXmlUtilities: getOrgData: Could not find node for product key path '${productKeytPath}'!`,
        );
    }
    const productKey = productKeyNode.textContent ?? '';

    const doctypeKeyPath = '/document/meta/doctypekey';
    const doctypeKeyNode = getFirstNode(doc, doc, doctypeKeyPath);
    if (!isDefined(doctypeKeyNode)) {
        throw new Error(
            `CosimaXmlUtilities: getOrgData: Could not find node for doctype key path '${doctypeKeyPath}'!`,
        );
    }
    const doctypeKey = doctypeKeyNode.textContent ?? '';

    const modalityPath = '/document/meta/modality';
    const modalityNode = getFirstNode(doc, doc, modalityPath);
    if (!isDefined(modalityNode)) {
        throw new Error(`CosimaXmlUtilities: getOrgData: Could not find node for modality path '${modalityPath}'!`);
    }
    const modality = modalityNode.textContent ?? '';

    const defaultInfoClassPath = '/document/meta/defaultinfoclass';
    const defaultInfoClassNode = getFirstNode(doc, doc, defaultInfoClassPath);
    if (!isDefined(defaultInfoClassNode)) {
        throw new Error(
            `CosimaXmlUtilities: getOrgData: Could not find node for defaultInfoClass path '${defaultInfoClassPath}'!`,
        );
    }
    const defaultInfoClass = defaultInfoClassNode.textContent ?? '';

    const versionPath = '/document/meta/version';
    const versionNode = getFirstNode(doc, doc, versionPath);
    if (!isDefined(versionNode)) {
        throw new Error(`CosimaXmlUtilities: getOrgData: Could not find node for version path '${versionPath}'!`);
    }
    const version = versionNode.textContent ?? '';
    return {
        product,
        productType,
        variant,
        productKey,
        doctypeKey,
        modality,
        defaultInfoClass,
        version,
    };
}

function getHeaderColumns(doc: XMLDocument, headerNodes: Element): MeasuringEquipmentHeaderColumn[] {
    const headerColumns: MeasuringEquipmentHeaderColumn[] = [];
    const headerColumnPath = './th/p';
    const headerColumnNodes = getChildNodes(doc, headerNodes, headerColumnPath);
    headerColumnNodes.forEach((column) => {
        const headerColumn: MeasuringEquipmentHeaderColumn = {
            xmlId: column.getAttribute('id') || '',
            columnName: column?.textContent || '',
        };

        headerColumns.push(headerColumn);
    });
    return headerColumns;
}

function getHeaders(doc: XMLDocument, topic: Element): MeasuringEquipmentTableHeader {
    const headerPath = './content/div/table/thead/tr';
    const headerNodes = getFirstNode(doc, topic, headerPath);
    if (headerNodes === null) {
        throw new Error(`CosimaXmlUtilities: getHeaders: Could not find content root for path '${headerNodes}'!`);
    }
    const header: MeasuringEquipmentTableHeader = {
        xmlId: headerNodes?.getAttribute('id') || '',
        visibility: headerNodes?.getAttribute('visibility') === 'yes',
        columns: getHeaderColumns(doc, headerNodes),
    };

    return header;
}

function getColumns(doc: XMLDocument, row: Element): MeasuringEquipmentRowColumn[] {
    const columns: MeasuringEquipmentRowColumn[] = [];
    const columnPath = './td/input';
    const columnNodes = getChildNodes(doc, row, columnPath);
    columnNodes.forEach((column) => {
        const rowColumn: MeasuringEquipmentRowColumn = {
            columnId: column.getAttribute('id') ?? '',
            label: column.getAttribute('label') ?? '',
            columnName: column.getAttribute('column') ?? '',
            value: column.textContent || '',
        };
        columns.push(rowColumn);
    });
    return columns;
}

function getRows(doc: XMLDocument, topic: Element): MeasuringEquipmentTableRow[] {
    const rows: MeasuringEquipmentTableRow[] = [];
    const rowsPath = './content/div/table/tbody/tr';
    const rowNodes = getChildNodes(doc, topic, rowsPath);
    rowNodes.forEach((row) => {
        const equipmentRow: MeasuringEquipmentTableRow = {
            rowId: row.getAttribute('id') || '',
            visibility: row.getAttribute('visibility') === 'yes',
            columns: getColumns(doc, row),
        };
        rows.push(equipmentRow);
    });
    return rows;
}
function getMeasuringEquipments(doc: XMLDocument): MeasuringEquipments | undefined {
    const measuringEquipmentsPath = `/document/topics/topic[@type='measuringequipment']`;
    const measuringEquipmentsNode = getFirstNode(doc, doc, measuringEquipmentsPath);

    if (!isDefined(measuringEquipmentsNode)) {
        // Not all forms contain measuring equipments, so we will continue with returning a
        // measuringEquipments Object only for forms which contain a measuring equipments node
        return undefined;
    }

    const equipmentsTitle = getFirstNode(doc, measuringEquipmentsNode, 'content/title');

    const measuringEquipments: MeasuringEquipments = {
        ...initialMeasuringEquipments,
        heading: equipmentsTitle?.textContent?.trim() || '',
        xmlId: measuringEquipmentsNode.getAttribute('id') || '',
        visibility: measuringEquipmentsNode.getAttribute('visibility') === 'yes',
        header: getHeaders(doc, measuringEquipmentsNode),
        rows: getRows(doc, measuringEquipmentsNode),
    };
    measuringEquipments.noMeasurementToolsUsed = measuringEquipments.rows[0].columns.some(
        (column) => column.value === FormsConstants.NA,
    );
    measuringEquipments.canSubmitForm = !measuringEquipments.rows.some(
        (row) => row.visibility && row.columns.some((column) => column.value === ''),
    );

    return measuringEquipments;
}

function getCopyright(doc: XMLDocument): Copyright {
    const copyrightPath = '/document/meta/copyrightholder';
    const copyrightNode = getFirstNode(doc, doc, copyrightPath);
    if (!isDefined(copyrightNode)) {
        throw new Error(`CosimaXmlUtilities: getCopyright: Could not find node for copyright path '${copyrightPath}'`);
    }
    const copyright = copyrightNode.textContent ?? '';

    return {
        line1: copyright,
    };
}

function getUpdatedTypeFilters(functionalElements: FunctionalElements, typeFilters: FormTypeFilters): FormTypeFilters {
    const initialTypeFilters = { ...typeFilters };

    const updatedFilters: { [key in FormsConstants.ELEMENT_TYPE]: TypeFilter } = { ...initialFormTypeFilters };
    Object.values(initialTypeFilters).forEach((filter) => {
        const matchingFunctionalElement = Object.values(functionalElements).find(
            (functionalElement) => functionalElement.elementType === filter.elementType,
        );

        updatedFilters[filter.elementType] = {
            ...initialTypeFilters[filter.elementType],
            active: isDefined(matchingFunctionalElement),
        };
    });

    return updatedFilters;
}

function parseTopic(
    doc: XMLDocument,
    topic: Element,
    parent: ContentId,
    headings: Mutable<Headings>,
    functionalElements: Mutable<FunctionalElements>,
    contentBlocks: Mutable<ContentBlocks>,
    optionDependencies: Mutable<OptionDependencies>,
): ContentBlock {
    const id = xmlNodeIdCounter++;
    const xmlId = topic.getAttribute('id') ?? '';

    const contentPath = './content';
    const contentNode = getFirstNode(doc, topic, contentPath);
    if (!isDefined(contentNode)) {
        throw new Error(`CosimaXmlUtilities: parseTopic: Could not find content on node ${xmlId}`);
    }

    const topicsPath = './topics/topic';
    const topicsElements = getChildNodes(doc, topic, topicsPath);

    const contentChildren: ContentChild[] = [];

    const heading = getHeading(doc, contentNode, id); // sect ion number + heading
    headings[heading.id] = heading;
    contentChildren.push({ id: heading.id, type: CONTENT_TYPE.HEADING });

    const metaPath = './meta/materialnumbers';
    const metaNode = getFirstNode(doc, topic, metaPath);
    let topicMaterialNumbers: string[] = [];
    if (isDefined(metaNode)) {
        const materialNumbers = getTopicMaterialNumbers(doc, metaNode);
        topicMaterialNumbers = materialNumbers.map((materialNumber) => materialNumber.textContent ?? '');
    }

    const eResults = getFunctionalElements(doc, contentNode, id);

    const cosimaOptionIndex = eResults.findIndex((eResult) => isCosimaOption(eResult));
    const optionPresent = eResults[cosimaOptionIndex];

    // spli ce is changing the array, hence, we need to create a new one, spreading the eResults
    const optionalElements = [...eResults];
    // remo ve from the eResults the option Present element if an option present was found
    if (cosimaOptionIndex >= 0) {
        optionalElements.splice(cosimaOptionIndex, 1);
    }

    if (isDefined(optionPresent)) {
        optionDependencies[optionPresent.id] = {
            option: optionPresent.id,
            checkpoints: [], // in c osima there are no checkpoints available
            functionalElements: optionalElements.map((opEl) => opEl.id),
        };
    }

    eResults.forEach((f) => (functionalElements[f.id] = f));
    contentChildren.push(...eResults.map((e) => ({ id: e.id, type: CONTENT_TYPE.FUNCTIONAL })));

    const childTopics = topicsElements.map((childTopic) =>
        parseTopic(doc, childTopic, id, headings, functionalElements, contentBlocks, optionDependencies),
    );

    const additionalDependencies: ContentId[] = [];
    childTopics.forEach((t) => {
        // sele ct only functional Elements
        const functionalElementDependencies = t.children
            .filter((child) => child.type === CONTENT_TYPE.FUNCTIONAL)
            .map((f) => f.id);
        additionalDependencies.push(...functionalElementDependencies);

        contentBlocks[t.id] = t;
    });
    contentChildren.push(...childTopics.map((c) => ({ id: c.id, type: CONTENT_TYPE.CONTENT })));

    if (isDefined(optionPresent)) {
        const dependency = optionDependencies[optionPresent.id];
        if (isDefined(dependency)) {
            optionDependencies[optionPresent.id] = {
                ...dependency,
                option: optionPresent.id,
                functionalElements: [...dependency.functionalElements, ...additionalDependencies],
            };
        }
    }

    const content: ContentBlock = {
        id,
        children: contentChildren,
        parentId: parent,
        visible: getVisibility(topic),
        xmlId,
        topicMaterialNumbers: topicMaterialNumbers,
    };

    return content;
}

function getFormContent(doc: XMLDocument): {
    headings: Mutable<Headings>;
    functionalElements: Mutable<FunctionalElements>;
    contentBlocks: Mutable<ContentBlocks>;
    optionDependencies: Mutable<OptionDependencies>;
} {
    // Init ialize data structures
    const headings: Mutable<Headings> = {};
    const functionalElements: Mutable<FunctionalElements> = {};
    const contentBlocks: Mutable<ContentBlocks> = {};
    const optionDependencies: Mutable<OptionDependencies> = {};

    // Get  top level content blocks
    const contentRootPath = '/document/topics';
    const contentRootNodes = getChildNodes(doc, doc, contentRootPath);
    if (contentRootNodes.length === 0) {
        throw new Error(
            `CosimaXmlUtilities: getFormContent: Could not find content root for path '${contentRootPath}'!`,
        );
    }

    contentRootNodes.forEach((rootNode) => {
        const topicPath = './topic';
        const topicElements = getChildNodes(doc, rootNode, topicPath);

        topicElements
            .filter((topicNode) => {
                const type = topicNode.getAttribute('type') ?? '';
                return type === 'content'; // othe r types are customer identification, "introduction of report", measuringequipment
            })
            .forEach((topicElement) => {
                const contentBlock = parseTopic(
                    doc,
                    topicElement,
                    -1,
                    headings,
                    functionalElements,
                    contentBlocks,
                    optionDependencies,
                );
                contentBlocks[contentBlock.id] = contentBlock;
            });
    });

    // we a re supporting only one employee signature per form
    if (multipleSignaturesAvailable(functionalElements, FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE)) {
        throw new Error(
            `CosimaXmlUtilities: getFormContent: more than one ${FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE} available in form`,
        );
    }

    // we a re supporting only one employee signature per form
    if (multipleSignaturesAvailable(functionalElements, FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE)) {
        throw new Error(
            `CosimaXmlUtilities: getFormContent: more than one ${FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE} available in form`,
        );
    }

    return {
        headings,
        functionalElements,
        contentBlocks,
        optionDependencies,
    };
}

function getSystemCondition(doc: XMLDocument): SystemCondition | undefined {
    const systemConditionContentPath = `/document/topics/topic[@type='systemcondition']/content`;

    const systemConditionContentNode = getFirstNode(doc, doc, systemConditionContentPath);
    if (!isDefined(systemConditionContentNode)) {
        throw new Error(
            `CosimaXmlUtilities: getSystemCondition: Could not find node for system condition path '${systemConditionContentPath}'`,
        );
    }

    const standardFunctionalElementPath = `./e-result[@inputtype='standard']`;
    const standardFunctionalElementNode = getFirstNode(doc, systemConditionContentNode, standardFunctionalElementPath);

    if (!isDefined(standardFunctionalElementNode)) {
        throw new Error(
            `CosimaXmlUtilities: getSystemCondition: Could not find the standard functional element for system condition`,
        );
    }
    const standardFunctionalElement = getStandardFunctionalElement(
        doc,
        standardFunctionalElementNode,
        -1,
        FUNCTIONAL_ELEMENT_TYPE.STANDARD,
    );

    const choices = standardFunctionalElement.choices;

    if (!isDefined(choices)) {
        throw new Error(
            `CosimaXmlUtilities: getSystemCondition: could not find the standardFunctionalElement with choices '${standardFunctionalElement}'`,
        );
    }

    const approverSignatureElementPath = `./e-result[@inputtype='approver_signature']`;
    const approverSignatureElementNode = getFirstNode(doc, systemConditionContentNode, approverSignatureElementPath);
    if (!isDefined(approverSignatureElementNode)) {
        throw new Error(`CosimaXmlUtilities: getSystemCondition: could not find the approver signature node`);
    }

    const customerSignature = getSignatureFunctionalElement(
        doc,
        approverSignatureElementNode,
        -1,
        FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE,
    );

    if (!isDefined(customerSignature)) {
        throw new Error(`CosimaXmlUtilities: getSystemCondition: could not find approver signature`);
    }

    const employeeSignatureElementPath = `./e-result[@inputtype='employee_signature']`;
    const employeeSignatureElementNode = getFirstNode(doc, systemConditionContentNode, employeeSignatureElementPath);
    if (!isDefined(employeeSignatureElementNode)) {
        throw new Error(`CosimaXmlUtilities: getSystemCondition: could not find the approver signature`);
    }

    const employeeSignature = getSignatureFunctionalElement(
        doc,
        employeeSignatureElementNode,
        -1,
        FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE,
    );

    if (!isDefined(customerSignature)) {
        throw new Error(`CosimaXmlUtilities: getSystemCondition: could not find customer signature`);
    }

    return {
        choices: standardFunctionalElement.choices,
        choicesFunctionalElementXmlId: standardFunctionalElement.xmlId,
        customerSignature,
        deficienciesSelection: DEFICIENCY_SELECTION.NO_SELECTION,
        deficienciesXmlId: '',
        employeeSignature,
        heading: standardFunctionalElement.label,
        imagingRemark: '',
        noDeficienciesLabel: choices.choicePositive?.label ?? '',
        seriousDeficienciesLabel: choices.choiceNa?.label ?? '',
        slightDeficienciesLabel: choices.choiceNegative?.label ?? '',
        remarksLabel: standardFunctionalElement.remarksLabel ?? '',
        remarksValue: standardFunctionalElement.remarks ?? '',
        remarksXmlId: standardFunctionalElement.remarksXmlId ?? '',
    };
}

function getDefectServicePartFieldByElementName(doc: XMLDocument, elementName: string): DefectServicePartInputField {
    const valueElement = getFirstNode(
        doc,
        doc,
        `/document/topics/topic[@id='defectservicepartidentification']/content/${elementName}`,
    );
    if (!valueElement) {
        throw new Error(
            `CosimaXmlUtilities: getDefectiveServicePartFieldByElementName: Could not find node for field '${elementName}'`,
        );
    }
    const label = valueElement.getAttribute('label') ?? '';
    const value = valueElement.textContent ?? '';

    return {
        xmlId: elementName,
        value,
        label,
    };
}
function getDefectServicePartIdentification(doc: XMLDocument): DefectServicePartIdentification {
    const titleElement = getFirstNode(
        doc,
        doc,
        `/document/topics/topic[@id='defectservicepartidentification']/content/title`,
    );

    if (!titleElement) {
        throw new Error(`CosimaXmlUtilities: getDefectServicePartIdentification: Could not find title node`);
    }

    const title = titleElement.textContent ?? '';
    const materialNo = getDefectServicePartFieldByElementName(doc, 'materialno');
    const serialNo = getDefectServicePartFieldByElementName(doc, 'serialno');
    const orderNo = getDefectServicePartFieldByElementName(doc, 'orderno');

    return {
        title,
        materialNo,
        serialNo,
        orderNo,
    };
}
export class CosimaXmlUtilities {
    // can  be used to reset CosimaXmlUtilities for testing
    public static reset(): void {
        xmlNodeIdCounter = 0;
    }

    public static updateFunctionalElementSelection(
        doc: XMLDocument,
        functionalElementXmlId: string | undefined,
        choiceXmlId: string | undefined,
    ): void {
        // Get  e-result for functional element
        const eresult = getFirstNode(doc, doc, `//e-result[@id='${functionalElementXmlId}']`);
        if (!isDefined(eresult)) {
            throw new Error(
                `CosimaXmlUtilities: updateFunctionalElementSelection: Could not find e-result element with id '${functionalElementXmlId}'`,
            );
        }

        // Iter ate over all choices and set selected to false
        const choices = getChildNodes(doc, eresult, `./choices/*`);

        // A fu nctional element can exist without choices. In case we don't find any choices on the eresult, we continue.
        if (choices.length === 0) {
            return;
        }

        choices.forEach((choice) => {
            // Chec k if choice node actually is a choice
            if (!choice.tagName.startsWith('choice_')) {
                throw new Error(
                    `CosimaXmlUtilities: updateFunctionalElementSelection: Found choices child that is not a choice element for e-result with id '${functionalElementXmlId}'`,
                );
            }

            // set  selected to false
            choice.setAttribute('selected', 'false');
        });

        // Upda ted selected attribute of given e-result
        const selectedChoice = choices.find((c) => c.getAttribute('id') === choiceXmlId);
        selectedChoice?.setAttribute('selected', 'true');

        // Add  modified attribute to e-result
        eresult.setAttribute('modified', moment().toISOString());
    }

    public static updateInputField(doc: XMLDocument, xmlId: string, newValue: string): void {
        // find matching XML element
        let inputField = getFirstNode(doc, doc, `//*[@id='${xmlId}']`);
        if (!isDefined(inputField)) {
            // are  we maybe trying to update an input field in the customer identification?
            // if y es, then we need to interpret the xmlId as element name, not as attribute value
            const customerIdentificationInputField = getChildNodes(
                doc,
                doc,
                `//topic[@id='customeridentification']/content/${xmlId}`,
            );
            if (customerIdentificationInputField.length !== 1) {
                throw new Error(
                    `CosimaXmlUtilities: updateInputField: Could not find any element with id '${xmlId}'. Was also unable to find it in customer identification!`,
                );
            }
            inputField = customerIdentificationInputField[0];
        }

        // upda te input field
        inputField.textContent = newValue;
    }

    public static updateDefectServicePartInputField(doc: XMLDocument, xmlId: string, newValue: string): void {
        const defectServicePartInputField = getChildNodes(
            doc,
            doc,
            `//topic[@id='defectservicepartidentification']/content/${xmlId}`,
        );

        if (defectServicePartInputField.length !== 1) {
            throw new Error(
                `CosimaXmlUtilities: updateDefectServicePartInputField: Could not find the element '${xmlId}' in the defect service part identification!`,
            );
        }

        const inputField = defectServicePartInputField[0];
        inputField.textContent = newValue;
    }

    public static updateElementVisibility(
        doc: XMLDocument,
        elementXmlId: string | undefined,
        visibility: boolean,
    ): void {
        // if the xml id of the field is an empty string, then we don't want to search for the field.
        // also there are some functional elements like signatures which do not have the remarks fields
        if (!isDefined(elementXmlId) || elementXmlId.length === 0) {
            return;
        }

        const field = getFirstNode(doc, doc, `//*[@id='${elementXmlId}']`);

        if (!isDefined(field)) {
            throw new Error(
                `CosimaXmlUtilities: updateElementVisibility: Could not find field with id '${elementXmlId}'`,
            );
        }

        field.setAttribute('visibility', visibility ? 'yes' : 'no');
    }

    public static updateDropdownOptionSelection(
        doc: XMLDocument,
        elementXmlId: string | undefined,
        selected: boolean,
    ): void {
        // if the xml id of the field is an empty string, then we don't want to search for the field.
        // also there are some functional elements like signatures which do not have the remarks fields
        if (!isDefined(elementXmlId) || elementXmlId.length === 0) {
            return;
        }

        const field = getFirstNode(doc, doc, `//*[@id='${elementXmlId}']`);

        if (!isDefined(field)) {
            throw new Error(
                `CosimaXmlUtilities: updateDropdownOptionSelection: Could not find field with id '${elementXmlId}'`,
            );
        }

        field.setAttribute('selected', selected ? 'true' : 'false');
    }

    public static hideOptionPresentElements(doc: XMLDocument): void {
        const pdfFormElements = getChildNodes(doc, doc, `//e-result[@inputtype='option_present']`);
        pdfFormElements.forEach((element) => {
            const xmlId = element.getAttribute('id');
            if (!isDefined(xmlId)) {
                throw new Error(
                    `CosimaXmlUtilities: hideOptionPresentElements: Could not find field with id '${xmlId}'`,
                );
            }

            CosimaXmlUtilities.updateElementVisibility(doc, xmlId, false);
        });
    }

    public static getContent(
        doc: XMLDocument,
        type: FormsConstants.FORMTYPE,
        typeFilters: FormTypeFilters,
    ): ContentResults {
        const unusedStatePropertiesFromAstoria = {
            checkpointDependencies: {}, // not  used for Cosima; dependencies are handled within the functional elements
            checkpoints: {}, // not  used for Cosima; instead we use functional elements
            options: {}, // do n ot use for Cosima; instead use functional element
        };

        const content = getFormContent(doc);

        const systemCondition =
            type === FormsConstants.FORMTYPE.MAINTENANCE_REPORT ||
            type === FormsConstants.FORMTYPE.SAFETY_RELATED_TEST_REPORT
                ? getSystemCondition(doc)
                : undefined;

        const updatedTypeFilters = getUpdatedTypeFilters(content.functionalElements, typeFilters); // rese arch how the types are represented in Cosima

        // SPQ  does not contain Customer Identification section
        const customerIdentification = FormUtilities.isServicePartQuestionnaire(type)
            ? initialCosimaCustomerIdentification
            : getCustomerIdentification(doc);
        const remarksSection = getRemarksSection(doc);
        const orgData = getOrgData(doc);
        const copyright = getCopyright(doc);

        const defectServicePartIdentification = FormUtilities.isServicePartQuestionnaire(type)
            ? getDefectServicePartIdentification(doc)
            : undefined;

        const enableManualIndividualization = false;
        const answers = AnswerUtilities.getCompleteAnswers(
            FormsConstants.MASTER_PARTICIPANT_ID,
            content.contentBlocks,
            unusedStatePropertiesFromAstoria.checkpoints, // not  in Cosima
            unusedStatePropertiesFromAstoria.options, // not  in Cosima
            content.functionalElements,
            unusedStatePropertiesFromAstoria.checkpointDependencies, // not  in Cosima
            updatedTypeFilters,
            initialFormFilters,
            enableManualIndividualization,
            type,
        );
        const individualization = getIndividualization(doc);
        const measuringEquipments = getMeasuringEquipments(doc);

        return {
            ...unusedStatePropertiesFromAstoria,
            ...content,
            answers,
            systemCondition,
            doctype: undefined, // not  used in Cosima
            hintsForFilling: undefined, // does not exist as separate section in Cosima
            customerIdentification,
            remarksSection,
            infoClass: FormsConstants.INFOCLASS.UNKNOWN, // only used by Astoria
            orgData,
            copyright,
            typeFilters: updatedTypeFilters,
            defectServicePartIdentification,
            individualization,
            measuringEquipments,
        };
    }

    /**
     * Removes the attribute MODIFIED from all PDFFORM elements
     *
     * @param doc the XML document from which the modified attribute
     */
    public static removeModifiedAttribute(doc: XMLDocument): void {
        const pdfFormElements = getChildNodes(doc, doc, '//e-result[@modified]');

        pdfFormElements.forEach((element) => element.removeAttribute('modified'));
    }

    /**
     * Remove all e-results elements outside of a given date range
     * This functionality was removed from the scope of MARS2, but will probably be needed in the future. Hence keeping
     * the code in here for future use.
     *
     * @param doc the XML document
     * @param fromDate the date from which to keep the e-results
     * @param tillDate the date until which to keep the e-results
     */
    public static removeByDate(doc: XMLDocument, fromDate: moment.Moment, tillDate: moment.Moment): void {
        // Loop over all functional elements (=LOP tables) and check their MODIFIED date
        const functionalElements = getChildNodes(doc, doc, '//e-result');

        // Get e-results elements inside functional element and check modified date
        functionalElements.forEach((f) => {
            const removeFunctionalElement = hasPdfFormOutsideDateRange(f, fromDate, tillDate);

            if (removeFunctionalElement && f.parentElement !== null) {
                f.parentElement.removeChild(f);
            }
        });
    }

    public static removePreviousInterimContent(doc: XMLDocument, functionalElements: FunctionalElements): void {
        const functionalElementsFromPreviousInterim: FunctionalElement[] = Object.values(functionalElements).filter(
            (f) => f.contentLoadedFromXml,
        );

        // Iter ate over functional elements whose content was taken from previous interim XML and remove their XML nodes
        functionalElementsFromPreviousInterim.forEach((f) => {
            let xmlId = '';
            switch (f.type) {
                case FUNCTIONAL_ELEMENT_TYPE.STANDARD:
                case FUNCTIONAL_ELEMENT_TYPE.REMARK:
                    const standardXmlId = (f as StandardFunctionalElement).xmlId;
                    if (!isDefined(standardXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the standard functional element`,
                        );
                    }
                    xmlId = standardXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.COMPONENT:
                    const materialSerialNumberXmlId = (f as MaterialSerialNumber).xmlId;
                    if (!isDefined(materialSerialNumberXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the material serial number functional element`,
                        );
                    }
                    xmlId = materialSerialNumberXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE:
                case FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE:
                    const signatureXmlId = (f as Signature).xmlId;
                    if (!isDefined(signatureXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the signature functional element`,
                        );
                    }
                    xmlId = signatureXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.DATE:
                    const dateXmlId = (f as DatePerformed).xmlId;
                    if (!isDefined(dateXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the date functional element`,
                        );
                    }
                    xmlId = dateXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.DEFECTIVE_COMPONENT_REPLACEMENT:
                    const defectiveComponentReplacementXmlId = (f as DefectiveComponentReplacement).xmlId;
                    if (!isDefined(defectiveComponentReplacementXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the defective component replacement functional element`,
                        );
                    }
                    xmlId = defectiveComponentReplacementXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.VALUE:
                    const valueXmlId = (f as ValueFunctionalElement).xmlId;
                    if (!isDefined(valueXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the value functional element`,
                        );
                    }
                    xmlId = valueXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.OPTION_PRESENT:
                    const optionPresentXmlId = (f as CosimaOption).xmlId;
                    if (!isDefined(optionPresentXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the option present functional element`,
                        );
                    }
                    xmlId = optionPresentXmlId;
                    break;
                case FUNCTIONAL_ELEMENT_TYPE.DROP_DOWN_OPTIONS:
                    const dropDownOptionsXmlId = (f as DropDownOptionsFunctionalElement).xmlId;
                    if (!isDefined(dropDownOptionsXmlId)) {
                        throw new Error(
                            `CosimaXmlUtilities: removePreviousInterimContent: missing xmlId for the dropdown options functional element`,
                        );
                    }
                    xmlId = dropDownOptionsXmlId;
                    break;

                // all
                case FUNCTIONAL_ELEMENT_TYPE.DATE_PERFORMED:
                case FUNCTIONAL_ELEMENT_TYPE.DISCREPANCY_LOG:
                case FUNCTIONAL_ELEMENT_TYPE.VALUE_UNIT:
                case FUNCTIONAL_ELEMENT_TYPE.OPTION:
                case FUNCTIONAL_ELEMENT_TYPE.WORKSTEPS_PERFORMED:
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE_SYSTEM_CONDITION:
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER_SYSTEM_CONDITION:
                case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO:
                case FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC:
                case FUNCTIONAL_ELEMENT_TYPE.LAST_PROCEDURE_DATE:
                case FUNCTIONAL_ELEMENT_TYPE.REPLACEMENT_DATE:
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER:
                case FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_EMPLOYEE:
                    throw new Error(
                        `CosimaXmlUtilities: removePreviousInterimContent: Functional element type '${f.type}' not supported for Cosima!`,
                    );

                default:
                    assertUnreachable(f.type);
            }

            const functionalElementPath = `//e-result[@id='${xmlId}']`;
            const functionalElementNode = getFirstNode(doc, doc, functionalElementPath);

            if (functionalElementNode !== null && functionalElementNode.parentElement !== null) {
                functionalElementNode.parentElement.removeChild(functionalElementNode);
            }
        });
    }

    public static removeByAnswer(doc: XMLDocument): void {
        // Loop over all e-results and check if they have a choice answered with "NO" or "NA"
        const functionalElements = getChildNodes(doc, doc, '//e-result');
        functionalElements.forEach((f) => {
            // Sign atures will never be removed
            const eResultType = f.getAttribute('inputtype')?.toUpperCase() ?? '';
            if (
                eResultType === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE ||
                eResultType === FUNCTIONAL_ELEMENT_TYPE.EMPLOYEE_SIGNATURE
            ) {
                return;
            }
            const choices = getChoices(doc, f);

            const removeFunctionalElement =
                (choices.choiceNegative?.selectedInMaster && choices.choiceNegative?.selected) ||
                (choices.choiceNa?.selectedInMaster && choices.choiceNa?.selected);

            if (removeFunctionalElement && f.parentElement !== null) {
                f.parentElement.removeChild(f);
            }
        });
    }

    public static updateOptionDependencies(
        doc: XMLDocument,
        contentId: ContentId,
        functionalElement: FunctionalElement,
        dependencies: {
            checkpointOptions: CheckpointSelection[];
            functionalElements: FunctionalElement[];
        },
    ): void {
        const selectedChoice = Object.values(functionalElement.choices ?? {}).find((c) => c?.selected);
        if (isDefined(selectedChoice) && selectedChoice.type === CHOICE_TYPE.CHOICE_NEGATIVE) {
            dependencies.functionalElements.forEach((f) => {
                // nest ed child options don't have a NA_CHOICE, therefore we also selected CHOICE_NEGATIVE
                const choiceXmlId = isCosimaOption(f) ? f.choices.choiceNegative?.xmlId : f.choices?.choiceNa?.xmlId;
                return CosimaXmlUtilities.updateFunctionalElementSelection(doc, f.xmlId, choiceXmlId);
            });
        } else {
            dependencies.functionalElements.forEach((f) =>
                CosimaXmlUtilities.updateFunctionalElementSelection(doc, f.xmlId, undefined),
            );
        }
    }
}

export const testableCosimaXmlUtilitiesFunctions = {
    getCustomerIdentification,
    getOrgData,
    getCopyright,
    getFormContent,
    getSystemCondition,
    getUpdatedTypeFilters: getUpdatedTypeFilters,
    getDateFunctionalElement,
    getChoices,
    getComponentFunctionalElement,
    getValueFunctionalElement,
    getDefectiveComponentReplacementFunctionalElement,
    getSignatureFunctionalElement,
    getOption,
    getVisibility,
    getAnswerSet,
    parseDateInOneOfTheKnownLanguages,
};
